import React from "react";
import { List, ListItemButton, ListItemText, Collapse, Box, Link as MuiLink } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Category } from "../../../../types/Category";
import { CategoryItem } from "./CategoryItem";
import Searchbar from "../Searchbar";
import { interceptLeftClick } from "@ecommerce/shared-utils";
import { STYLES } from "../../../ShoppingCart/ShoppingCartStyles";
import { useTemplateConfig } from "../../../../templates/templateConfigContext";

interface CategoriesMenuProps {
    rootCategories: Category[];
    productosOpen: boolean;
    expandedCategories: number[];
    onNavigate: (path: string) => void;
    onToggleProductos: () => void;
    onToggleCategory: (categoryId: number) => void;
    onCategoryClick: (category: Category) => void;
    onViewAllClick: (category: Category) => void;
    getSubcategories: (categoryId: number) => Category[];
    isMobile: boolean;
    onClose?: () => void;
}

export const CategoriesMenu: React.FC<CategoriesMenuProps> = ({
    rootCategories,
    productosOpen,
    expandedCategories,
    onNavigate,
    onToggleProductos,
    onToggleCategory,
    onCategoryClick,
    onViewAllClick,
    getSubcategories,
    isMobile,
    onClose,
}) => {
    const { config, bitContentEditMode } = useTemplateConfig();
    const faqsVisible = config.faqsConfig?.bitVisible !== false;
    const faqsLabel = config.faqsConfig?.vchLabelMenu ?? "FAQs";
    const renderCategory = (category: Category, level: number = 0): React.ReactNode => {
        const subcategories = getSubcategories(category.id);
        const isExpanded = expandedCategories.includes(category.id);

        return (
            <CategoryItem
                key={category.id}
                category={category}
                subcategories={subcategories}
                isExpanded={isExpanded}
                level={level}
                onToggle={onToggleCategory}
                onCategoryClick={onCategoryClick}
                onViewAllClick={onViewAllClick}
                renderCategory={renderCategory}
            />
        );
    };

    const handleLinkClick = (onLeftClick: () => void) => (e: React.MouseEvent) => {
        const isLeftClick = e.button === 0;
        const isModified = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

        if (isLeftClick && !isModified) {
            e.preventDefault();
            onLeftClick();
        }
    };

    return (
        <>
            {isMobile && (
                <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                    <Searchbar onClose={onClose} />
                </Box>
            )}
            <List component="nav">
                <MuiLink
                    href="/"
                    underline="none"
                    color="inherit"
                    onClick={interceptLeftClick(() => onNavigate("/"))}
                    sx={{ display: "block" }}
                >
                    <ListItemButton>
                        <ListItemText
                            primary="Inicio"
                            primaryTypographyProps={{
                                fontSize: 16,
                                fontFamily: STYLES.fontFamily,
                                fontWeight: "bold",
                            }}
                        />
                    </ListItemButton>
                </MuiLink>

                <MuiLink
                    href="/productos"
                    underline="none"
                    color="inherit"
                    onClick={interceptLeftClick(onToggleProductos)}
                    sx={{ display: "block" }}
                >
                    <ListItemButton>
                        <ListItemText
                            primary="Productos"
                            primaryTypographyProps={{
                                fontSize: 16,
                                fontFamily: STYLES.fontFamily,
                                fontWeight: "bold",
                            }}
                        />
                        {productosOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </MuiLink>

                <Collapse in={productosOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <MuiLink
                            href="/productos"
                            underline="none"
                            color="inherit"
                            onClick={interceptLeftClick(() => onNavigate("/productos"))}
                            sx={{ display: "block" }}
                        >
                            <ListItemButton sx={{ pl: 3 }}>
                                <ListItemText
                                    primary="Ver todos los productos"
                                    primaryTypographyProps={{
                                        fontSize: 14,
                                        fontWeight: 400,
                                    }}
                                />
                            </ListItemButton>
                        </MuiLink>

                        {rootCategories.map((category) => renderCategory(category, 0))}
                    </List>
                </Collapse>

                {(faqsVisible || bitContentEditMode) && (
                    <MuiLink
                        href="/faqs"
                        underline="none"
                        color={!faqsVisible ? "text.disabled" : "inherit"}
                        onClick={interceptLeftClick(() => onNavigate("/faqs"))}
                        sx={{ display: "block", opacity: faqsVisible ? 1 : 0.45 }}
                    >
                        <ListItemButton>
                            <ListItemText
                                primary={faqsLabel}
                                primaryTypographyProps={{
                                    fontSize: 16,
                                    fontFamily: STYLES.fontFamily,
                                    fontWeight: "bold",
                                }}
                            />
                            {!faqsVisible && (
                                <VisibilityOffOutlinedIcon
                                    fontSize="small"
                                    sx={{ color: "text.disabled" }}
                                />
                            )}
                        </ListItemButton>
                    </MuiLink>
                )}
            </List>
        </>
    );
};
